/**
 * The tabbed record (docs/features/one-record-layout/01_mock_up.md, a mock-up approved by Ben on 5 Nov): the generic
 * record page's proposed layout, drawn when the record layout is `tabs` (`record-layout-mode.ts`). A short header
 * (title, states, the state's meaning, up to five key facts, the record's own commands and one grouped More…), the
 * lifecycle rail and any lead section, then one card of tabs: Details first, then a tab per child table and related
 * list, those with rows first. Every command sits where its subject is (`commandHomesOf`): the record's own in the
 * header, a list's on its tab, a setting's as its pencil on Details. The open tab is in the link (`?tab=lines`).
 */
import { ChevronDown, ChevronRight } from "lucide-react";
import {
  Fragment,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { useSearchParams } from "react-router";
import type {
  Command,
  ManifestObject,
} from "@shared-core/domain/object-ui/contract";
import {
  useRelatedCollectionCommands,
  useRelatedRowsOf,
  type RelatedRead,
} from "@shared-core/domain/object-ui/hooks";
import { useObjectManifest } from "@shared-core/domain/object-ui/manifest";
import type {
  PresentedChild,
  PresentedFact,
  PresentedRecord,
} from "@shared-core/domain/object-ui/record";
import {
  commandHomesOf,
  detailsSectionsOf,
  hasMoreMenu,
  headerMenuOf,
  keyFactsOf,
  openTabOf,
  recordTabsOf,
  tabHeadCommandsOf,
  tabStripOf,
  tabsThatFit,
  type CommandHome,
  type DetailsSection,
  type HeaderMenu,
  type RecordTab,
} from "@shared-core/domain/object-ui/record-layout";
import { declaredMainOf } from "@shared-core/domain/object-ui/row-commands";
import type {
  RecordSettings,
  Setting,
} from "@shared-core/domain/object-ui/settings";
import { MoreCommands } from "../../app/components/patterns/MoreCommands";
import {
  ObjectHeader,
  type Crumb,
} from "../../app/components/patterns/ObjectHeader";
import { Button } from "../../app/components/primitives/button";
import { Card } from "../../app/components/primitives/card";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "../../app/components/primitives/collapsible";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../../app/components/primitives/dropdown-menu";
import { Skeleton } from "../../app/components/primitives/skeleton";
import { useElementWidth } from "../../app/components/primitives/use-element-width";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
  TabsTriggerCount,
} from "../../app/components/primitives/tabs";
import {
  collectionCommandOpening,
  type OpenCommand,
} from "../../app/useCommandHost";
import { CommandButton, RecordNotices, SaidBlock } from "./record-parts";

/** The link's parameter naming the open tab. */
export const TAB_PARAM = "tab";

export function TabbedRecord({
  object,
  presented,
  settings,
  tableChildren,
  isPhone,
  context,
  crumbs,
  pills,
  subtitle,
  plainValueOf,
  settingOf,
  childBody,
  onRun,
  onOpen,
  between,
  after,
}: {
  object: ManifestObject;
  presented: PresentedRecord;
  /** The record's settings, the placement's overrides included. */
  settings: RecordSettings;
  /** The child tables drawn (a stacked child with no rows is not). */
  tableChildren: readonly PresentedChild[];
  isPhone: boolean;
  /** What heads a command's dialog: the record's heading. */
  context: string;
  crumbs: Crumb[];
  pills: ReactNode;
  subtitle: ReactNode;
  plainValueOf: (fact: PresentedFact) => ReactNode;
  settingOf: (
    fact: PresentedFact,
    setting: Setting,
    shownValue?: ReactNode,
  ) => ReactNode;
  childBody: (child: PresentedChild) => ReactNode;
  onRun: (command: Command) => void;
  onOpen: (open: OpenCommand) => void;
  /** Under the header, above the tabs: the lifecycle rail and any lead section. */
  between: ReactNode;
  /** Under the tabs: the record's custom sections and its timeline. */
  after: ReactNode;
}) {
  const index = useObjectManifest();
  const layout = object.record?.layout;
  const [searchParams, setSearchParams] = useSearchParams();
  const { reads, reload } = useRelatedRowsOf(presented.related);
  // A related list this person's role cannot see is left out, as the record leaves it out today.
  const related = presented.related.filter((r) => {
    const read = reads.get(r.object);
    return !(read?.status === "error" && read.httpStatus === 403);
  });
  const own = new Set(presented.commands.map((c) => c.name));
  const offered = useRelatedCollectionCommands(presented.related).filter(
    ({ command }) => !own.has(command.name),
  );
  const listOf = new Map(offered.map((o) => [o.command.name, o.related]));
  const alongside = offered.map((o) => o.command);
  const homes = commandHomesOf(index, {
    object,
    commands: [...presented.commands, ...alongside],
    offeredFor: new Map(
      offered.map((o) => [o.command.name, o.related.object] as const),
    ),
    childObjects: tableChildren.map((c) => c.object),
    relatedObjects: related.map((r) => r.object),
    factFields: presented.facts.map((f) => f.field),
  });
  const tabs = recordTabsOf(
    tableChildren.map((c) => ({
      object: c.object,
      label: c.label,
      field: object.children.find((d) => d.object === c.object)?.field,
      total: c.total,
    })),
    related.map((r) => {
      const read = reads.get(r.object);
      return {
        object: r.object,
        label: r.label,
        total: read?.status === "ready" ? read.value.total : null,
      };
    }),
    layout?.tabs,
  );
  const openTab = openTabOf(tabs, searchParams.get(TAB_PARAM), layout);
  const defaultTab = openTabOf(tabs, null, layout);
  const chooseTab = (slug: string) =>
    setSearchParams((current) => {
      const next = new URLSearchParams(current);
      if (slug === defaultTab.slug) next.delete(TAB_PARAM);
      else next.set(TAB_PARAM, slug);
      return next;
    });

  /** A command opened from this record; one a related list offers opens with the list's filters already in. */
  const run = (command: Command) => {
    const list = listOf.get(command.name);
    if (!list) return onRun(command);
    onOpen(
      collectionCommandOpening(index, command, {
        context: `${context} · ${list.label}`,
        params: list.params,
      }),
    );
  };

  const menu = headerMenuOf({
    own: presented.commands,
    alongside,
    homes,
    tabs,
    declaredMain: declaredMainOf(index, presented.commands, object.id),
    isPhone,
  });
  const tabCommandsOf = (tab: RecordTab) =>
    [...presented.commands, ...alongside].filter((c) => {
      const home: CommandHome | undefined = homes.get(c.name);
      return home?.kind === "tab" && home.tab === tab.id;
    });
  const keyFacts = keyFactsOf(presented.facts, layout);
  const sections = detailsSectionsOf(
    presented.facts,
    presented.factGroups,
    layout,
    (field) => settings.byField.has(field),
  );

  return (
    <>
      <ObjectHeader
        crumbs={crumbs}
        title={presented.heading}
        pills={pills}
        subtitle={subtitle}
        facts={[]}
      >
        {keyFacts.length > 0 && (
          <dl
            aria-label="Key facts"
            className="grid grid-cols-2 gap-x-4 gap-y-3 text-sm sm:flex sm:flex-wrap sm:gap-x-10"
          >
            {keyFacts.map((fact, i) => (
              <div key={`${fact.field}-${i}`} className="min-w-0 space-y-0.5">
                <dt className="text-xs text-muted-foreground">{fact.label}</dt>
                <dd className="break-words font-semibold text-foreground">
                  {plainValueOf(fact)}
                </dd>
              </div>
            ))}
          </dl>
        )}
        <HeaderCommands menu={menu} notices={presented.notices} onRun={run} />
      </ObjectHeader>
      <div className="min-w-0 space-y-4">
        {between}
        <Card className="gap-0 overflow-hidden">
          <Tabs
            value={openTab.slug}
            onValueChange={chooseTab}
            className="gap-0"
          >
            <TabBar
              tabs={tabs}
              open={openTab}
              isPhone={isPhone}
              onChoose={chooseTab}
            />
            <TabsContent
              value={openTab.slug}
              className="space-y-3 px-4 py-4 sm:px-6 sm:py-5"
            >
              {openTab.kind === "details" ? (
                <DetailsTab
                  sections={sections}
                  settings={settings}
                  isPhone={isPhone}
                  plainValueOf={plainValueOf}
                  settingOf={settingOf}
                />
              ) : (
                <ListTab
                  tab={openTab}
                  commands={tabCommandsOf(openTab)}
                  isPhone={isPhone}
                  onRun={run}
                >
                  {openTab.kind === "child" ? (
                    <ChildPanel
                      child={tableChildren.find(
                        (c) => c.object === openTab.id,
                      )!}
                      childBody={childBody}
                    />
                  ) : (
                    <RelatedPanel
                      label={openTab.label}
                      read={reads.get(openTab.id)}
                      childBody={childBody}
                      onRetry={reload}
                    />
                  )}
                </ListTab>
              )}
            </TabsContent>
          </Tabs>
        </Card>
        {after}
      </div>
    </>
  );
}

/**
 * The header's commands: notices, the main command's block, the buttons and one grouped More…. Nothing is drawn where
 * the record offers nothing to do.
 */
function HeaderCommands({
  menu,
  notices,
  onRun,
}: {
  menu: HeaderMenu;
  notices: PresentedRecord["notices"];
  onRun: (command: Command) => void;
}) {
  const hasMore = hasMoreMenu(menu);
  if (
    notices.length === 0 &&
    !menu.said &&
    menu.buttons.length === 0 &&
    !hasMore
  )
    return null;
  return (
    <div className="space-y-2 border-t border-border pt-3">
      <RecordNotices notices={notices} />
      <SaidBlock said={menu.said} />
      {(menu.buttons.length > 0 || hasMore) && (
        <div className="flex flex-wrap items-start gap-2">
          {menu.buttons.map((command) => (
            <CommandButton
              key={command.name}
              command={command}
              isPrimary={command === menu.primary}
              onRun={() => onRun(command)}
            />
          ))}
          {hasMore && <HeaderMoreMenu menu={menu} onRun={onRun} />}
        </div>
      )}
    </div>
  );
}

/**
 * The header's More…, grouped (the mock-up's rule): the header commands that didn't fit; every tab's commands under
 * the tab's name, so the menu doubles as an index of everything the record can do; the commands that end the record,
 * set apart in the destructive colour; and "Not available now" with each reason, one that several share said once.
 */
function HeaderMoreMenu({
  menu,
  onRun,
}: {
  menu: HeaderMenu;
  onRun: (command: Command) => void;
}) {
  const groups: ReactNode[] = [];
  if (menu.overflow.length > 0)
    groups.push(
      <Fragment key="overflow">
        {menu.overflow.map((c) => (
          <MenuCommand key={c.name} command={c} onRun={onRun} />
        ))}
      </Fragment>,
    );
  for (const { tab, commands } of menu.byTab)
    groups.push(
      <Fragment key={`tab-${tab.id}`}>
        <DropdownMenuLabel className="text-xs font-normal text-muted-foreground">
          {tab.label}
        </DropdownMenuLabel>
        {commands.map((c) => (
          <MenuCommand key={c.name} command={c} onRun={onRun} />
        ))}
      </Fragment>,
    );
  if (menu.last.length > 0)
    groups.push(
      <Fragment key="last">
        {menu.last.map((c) => (
          <MenuCommand key={c.name} command={c} onRun={onRun} isLast />
        ))}
      </Fragment>,
    );
  if (menu.unavailable.length > 0)
    groups.push(
      <Fragment key="unavailable">
        <DropdownMenuLabel className="text-xs font-normal text-muted-foreground">
          Not available now
        </DropdownMenuLabel>
        {menu.sharedSaid.map((reason) => (
          <p
            key={reason}
            className="px-2 pb-1 text-xs text-amber-700 dark:text-amber-400"
          >
            {reason}
          </p>
        ))}
        {menu.unavailable.map((c) => (
          <MenuCommand
            key={c.name}
            command={c}
            onRun={onRun}
            hidesReason={
              c.reason !== null && menu.sharedReasons.includes(c.reason)
            }
          />
        ))}
      </Fragment>,
    );
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button size="sm" variant="outline" className="max-sm:h-10">
          More…
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="start"
        className="max-h-[70vh] max-w-[min(22rem,90vw)] overflow-y-auto"
      >
        {groups.map((group, i) => (
          <Fragment key={i}>
            {i > 0 && <DropdownMenuSeparator />}
            {group}
          </Fragment>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function MenuCommand({
  command,
  onRun,
  isLast = false,
  hidesReason = false,
}: {
  command: Command;
  onRun: (command: Command) => void;
  /** Ends the record: drawn in the destructive colour. */
  isLast?: boolean;
  /** Its reason is said once above, for several commands. */
  hidesReason?: boolean;
}) {
  return (
    <DropdownMenuItem
      disabled={!command.enabled}
      variant={isLast ? "destructive" : "default"}
      onSelect={() => onRun(command)}
      className="flex-col items-start gap-0.5 py-2"
    >
      <span>{command.label}</span>
      {!command.enabled && command.reason && !hidesReason && (
        <span className="text-xs text-amber-700 dark:text-amber-400">
          {command.reason}
        </span>
      )}
    </DropdownMenuItem>
  );
}

/** A tab's words and count as the tab bar draws them, so the hidden copy measures what is shown. */
const TAB_TEXT =
  "inline-flex shrink-0 items-center gap-1.5 px-3 py-2 text-sm font-medium tracking-tight whitespace-nowrap";
/** The tab bar's padding (`px-4`) and the gap between its tabs (`gap-1`), in pixels. */
const BAR_PADDING = 32;
const TAB_GAP = 4;

/**
 * The tab bar, "priority+" (as GitHub's or Jira's): Details first, then the lists with rows (each with its count) as
 * long as they fit, then a final More ▾ holding the lists that didn't fit and, after a separator, every list with no
 * rows. A list chosen from More ▾ is shown in the panel and names More ▾ ("Recurring accruals ▾"). On a desktop the
 * room is measured (a ResizeObserver on the bar, a hidden copy of each tab for its width); on a phone the tabs scroll
 * sideways, keeping the open one in view, and More ▾ is the last.
 */
function TabBar({
  tabs,
  open,
  isPhone,
  onChoose,
}: {
  tabs: readonly RecordTab[];
  open: RecordTab;
  isPhone: boolean;
  onChoose: (slug: string) => void;
}) {
  const [barRef, barWidth] = useElementWidth<HTMLDivElement>();
  const measured = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const [widths, setWidths] = useState<{
    details: number;
    lists: number[];
    more: number;
  } | null>(null);
  const withRows = tabs.filter((t) => t.kind !== "details" && !t.isEmpty);
  const hasEmpty = tabs.some((t) => t.kind !== "details" && t.isEmpty);
  const listCount = tabs.length - 1;
  const measuredKey = withRows.map((t) => `${t.id}:${t.count}`).join("|");
  useLayoutEffect(() => {
    const layer = measured.current;
    if (!layer) return;
    const all = Array.from(layer.children).map(
      (child) => (child as HTMLElement).offsetWidth + TAB_GAP,
    );
    const next = {
      details: all[0] ?? 0,
      lists: all.slice(1, -1),
      more: all[all.length - 1] ?? 0,
    };
    setWidths((current) =>
      JSON.stringify(current) === JSON.stringify(next) ? current : next,
    );
  }, [measuredKey, listCount]);
  const fit =
    isPhone || !widths
      ? Infinity
      : tabsThatFit({
          available: barWidth === null ? null : barWidth - BAR_PADDING,
          detailsWidth: widths.details,
          widths: widths.lists,
          moreWidth: widths.more,
          hasEmpty,
        });
  const strip = tabStripOf(tabs, fit, open);
  const inMore = [...strip.overflow, ...strip.empty];
  const isOpenInMore = inMore.includes(open);
  useEffect(() => {
    const list = listRef.current;
    const active = list?.querySelector<HTMLElement>(
      "[data-state=active], [data-more-open]",
    );
    if (!list || !active) return;
    // Sideways only: never scroll the page to the tabs.
    const left = active.offsetLeft - list.offsetLeft;
    const right = left + active.offsetWidth;
    if (left < list.scrollLeft) list.scrollLeft = Math.max(0, left - 16);
    else if (right > list.scrollLeft + list.clientWidth)
      list.scrollLeft = right - list.clientWidth + 16;
  }, [open.slug, isOpenInMore]);
  return (
    <div ref={barRef} style={{ position: "relative" }}>
      {/* Each tab's width, measured out of sight: Details, the lists with rows, then More ▾ at its widest. Hidden by its
          own style, so it stays out of sight and out of the flow wherever no stylesheet has loaded. */}
      <div
        ref={measured}
        aria-hidden="true"
        className="flex"
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          width: "max-content",
          height: 0,
          overflow: "hidden",
          visibility: "hidden",
          pointerEvents: "none",
        }}
      >
        <span className={TAB_TEXT}>Details</span>
        {withRows.map((tab) => (
          <span key={tab.id} className={TAB_TEXT}>
            {tab.label}
            {tab.count !== null && (
              <TabsTriggerCount>{tab.count}</TabsTriggerCount>
            )}
          </span>
        ))}
        <span className={TAB_TEXT}>
          More ({listCount})
          <ChevronDown className="size-4" />
        </span>
      </div>
      <TabsList
        ref={listRef}
        variant="underline"
        aria-label="Record sections"
        className="px-2 sm:px-4"
      >
        {strip.shown.map((tab) => (
          <TabsTrigger key={tab.id} value={tab.slug}>
            {tab.label}
            {tab.count !== null && (
              <TabsTriggerCount>{tab.count}</TabsTriggerCount>
            )}
          </TabsTrigger>
        ))}
        {inMore.length > 0 && (
          <MoreTab
            overflow={strip.overflow}
            empty={strip.empty}
            open={isOpenInMore ? open : null}
            onChoose={onChoose}
          />
        )}
      </TabsList>
    </div>
  );
}

/**
 * The tab bar's last tab: a menu button in the tab bar, listing the lists it holds (those with rows first, with their
 * counts, then the empty ones). It reads "More (n)", or the name of the list chosen from it, underlined as the open tab.
 */
function MoreTab({
  overflow,
  empty,
  open,
  onChoose,
}: {
  overflow: readonly RecordTab[];
  empty: readonly RecordTab[];
  /** The list chosen from it that is open now, if any. */
  open: RecordTab | null;
  onChoose: (slug: string) => void;
}) {
  const item = (tab: RecordTab) => (
    <DropdownMenuItem
      key={tab.id}
      onSelect={() => onChoose(tab.slug)}
      aria-current={tab === open ? "true" : undefined}
      className={`justify-between gap-4 ${tab === open ? "font-semibold" : ""}`}
    >
      <span className={tab.isEmpty ? "text-muted-foreground" : undefined}>
        {tab.label}
      </span>
      {tab.count !== null && (
        <span className="text-xs tabular-nums text-muted-foreground">
          {tab.count}
        </span>
      )}
    </DropdownMenuItem>
  );
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          {...(open ? { "data-more-open": "" } : {})}
          className={`relative inline-flex shrink-0 items-center gap-1.5 rounded-sm px-3 py-2 text-sm font-medium tracking-tight whitespace-nowrap transition-colors after:absolute after:inset-x-2 after:bottom-0 after:h-0.5 after:content-[''] focus-visible:ring-2 focus-visible:ring-primary/40 focus-visible:outline-none ${
            open
              ? "text-foreground after:bg-foreground"
              : "text-muted-foreground hover:text-foreground"
          }`}
        >
          {open ? (
            <>
              {open.label}
              {open.count !== null && (
                <span className="inline-flex h-[1.125rem] min-w-[1.25rem] items-center justify-center rounded-full bg-foreground/10 px-1.5 text-[10px] font-medium tabular-nums">
                  {open.count}
                </span>
              )}
            </>
          ) : (
            `More (${overflow.length + empty.length})`
          )}
          <ChevronDown aria-hidden className="size-4" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="end"
        className="max-h-[70vh] min-w-56 overflow-y-auto"
      >
        {overflow.map(item)}
        {overflow.length > 0 && empty.length > 0 && <DropdownMenuSeparator />}
        {empty.map(item)}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

/** A child table's or related list's tab: its heading with the count, the commands that live there, and its rows. */
function ListTab({
  tab,
  commands,
  isPhone,
  onRun,
  children,
}: {
  tab: RecordTab;
  commands: readonly Command[];
  isPhone: boolean;
  onRun: (command: Command) => void;
  children: ReactNode;
}) {
  const head = tabHeadCommandsOf(commands, isPhone);
  const hasCommands =
    head.buttons.length > 0 ||
    head.more.length > 0 ||
    head.unavailable.length > 0;
  return (
    <>
      <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
        <h2 className="text-base font-semibold">
          {tab.label}
          {tab.count !== null && (
            <span className="ml-1 text-sm font-normal text-muted-foreground">
              ({tab.count})
            </span>
          )}
        </h2>
        {hasCommands && (
          <div className="flex flex-wrap items-start gap-2 sm:ml-auto max-sm:w-full">
            {head.buttons.map((command) => (
              <CommandButton
                key={command.name}
                command={command}
                isPrimary={false}
                onRun={() => onRun(command)}
              />
            ))}
            {(head.more.length > 0 || head.unavailable.length > 0) && (
              <MoreCommands
                commands={head.more}
                unavailable={head.unavailable}
                align="end"
                onRun={onRun}
              />
            )}
          </div>
        )}
      </div>
      {children}
    </>
  );
}

function ChildPanel({
  child,
  childBody,
}: {
  child: PresentedChild;
  childBody: (child: PresentedChild) => ReactNode;
}) {
  return (
    <>
      {child.note && (
        <p className="text-sm text-muted-foreground">{child.note}</p>
      )}
      {childBody(child)}
    </>
  );
}

function RelatedPanel({
  label,
  read,
  childBody,
  onRetry,
}: {
  label: string;
  read: RelatedRead | undefined;
  childBody: (child: PresentedChild) => ReactNode;
  onRetry: () => void;
}) {
  if (read?.status === "ready") return <>{childBody(read.value)}</>;
  if (read?.status === "error")
    return (
      <p className="text-sm text-destructive" role="alert">
        {`Couldn't load ${label.toLowerCase()}. `}
        <button
          type="button"
          onClick={onRetry}
          className="text-primary underline hover:no-underline"
        >
          Retry
        </button>
      </p>
    );
  return (
    <Skeleton
      role="status"
      className="h-16 w-full"
      aria-label={`Loading ${label.toLowerCase()}`}
    />
  );
}

/**
 * Details: every fact that is not a key fact, and every setting, in named sections, one per line, label on the left,
 * the value and its pencil or switch on the right. Three columns of sections on a wide screen; one on a phone, where
 * only the first starts open. A folded section shows what it holds in its heading. A setting that can't be changed now
 * shows its pencil disabled and its reason under the value.
 */
function DetailsTab({
  sections,
  settings,
  isPhone,
  plainValueOf,
  settingOf,
}: {
  sections: readonly DetailsSection[];
  settings: RecordSettings;
  isPhone: boolean;
  plainValueOf: (fact: PresentedFact) => ReactNode;
  settingOf: (
    fact: PresentedFact,
    setting: Setting,
    shownValue?: ReactNode,
  ) => ReactNode;
}) {
  if (sections.length === 0)
    return <p className="text-sm text-muted-foreground">None yet.</p>;
  return (
    <div className="grid items-start gap-3 lg:grid-cols-3">
      {sections.map((section, i) => (
        <DetailsSectionCard
          // A phone and a desktop open different sections: drawn afresh when the width changes.
          key={`${section.label}-${isPhone}`}
          section={section}
          isOpenAtFirst={!section.isFolded && (!isPhone || i === 0)}
        >
          {section.facts.map((fact, n) => {
            const setting = settings.byField.get(fact.field);
            const blocked = setting ? blockedReasonOf(setting) : null;
            const isStacked = fact.isLongText;
            return (
              <div
                key={`${fact.field}-${n}`}
                className={
                  isStacked
                    ? "space-y-1 py-1.5 text-sm"
                    : "grid grid-cols-[minmax(0,1fr)_minmax(0,1.3fr)] items-baseline gap-3 py-1.5 text-sm"
                }
              >
                <dt className="text-muted-foreground">{fact.label}</dt>
                <dd className="min-w-0 break-words font-medium">
                  {setting
                    ? settingOf(
                        fact,
                        setting,
                        // A long or formatted value reads as a block, its edit button after it.
                        fact.isLongText ? plainValueOf(fact) : undefined,
                      )
                    : plainValueOf(fact)}
                  {blocked && (
                    <span className="mt-0.5 block text-xs font-normal text-amber-700 dark:text-amber-400">
                      {blocked}
                    </span>
                  )}
                </dd>
              </div>
            );
          })}
        </DetailsSectionCard>
      ))}
    </div>
  );
}

/** Why a setting can't be changed now: its edit command's reason, or the reason of the command its switch would run. */
function blockedReasonOf(setting: Setting): string | null {
  if (setting.kind === "edit") {
    const first = setting.commands[0];
    return first && !first.enabled ? first.reason : null;
  }
  const next = setting.isOn ? setting.turnOff : setting.turnOn;
  return next && !next.command.enabled ? next.command.reason : null;
}

function DetailsSectionCard({
  section,
  isOpenAtFirst,
  children,
}: {
  section: DetailsSection;
  isOpenAtFirst: boolean;
  children: ReactNode;
}) {
  const [isOpen, setOpen] = useState(isOpenAtFirst);
  return (
    <Collapsible
      open={isOpen}
      onOpenChange={setOpen}
      className="rounded-lg border border-border"
    >
      <CollapsibleTrigger className="flex w-full items-baseline gap-2 px-3.5 py-2.5 text-left">
        <h3 className="text-sm font-semibold">{section.label}</h3>
        {!isOpen && (
          <span className="ml-auto text-xs text-muted-foreground">
            {section.summary}
          </span>
        )}
        <ChevronRight
          aria-hidden
          className={`size-4 shrink-0 self-center text-muted-foreground transition-transform ${isOpen ? "ml-auto rotate-90" : ""}`}
        />
      </CollapsibleTrigger>
      <CollapsibleContent>
        <dl className="px-3.5 pb-2.5">{children}</dl>
      </CollapsibleContent>
    </Collapsible>
  );
}
